import { createContext, useContext, useCallback, useState } from 'react';
import { useFormValidation } from '../hooks/useFormValidation';
import { ValidationRule } from '@/types/common';

interface FormContextValue {
  values: Record<string, unknown>;
  errors: Record<string, string>;
  setFieldValue: (field: string, value: unknown) => void;
  validate: () => boolean;
}

const FormContext = createContext<FormContextValue | null>(null);

interface FormProviderProps {
  children: React.ReactNode;
  initialValues: Record<string, unknown>;
  validationRules?: Record<string, ValidationRule[]>;
  onSubmit?: (values: Record<string, unknown>) => void;
}

export function FormProvider({
  children,
  initialValues,
  validationRules = {},
  onSubmit
}: FormProviderProps) {
  const [values, setValues] = useState<Record<string, unknown>>(() => ({ ...initialValues }));
  const { errors, validate } = useFormValidation(validationRules);

  const setFieldValue = useCallback((field: string, value: unknown) => {
    setValues(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (validate() && onSubmit) {
      onSubmit(values);
    }
  }, [validate, onSubmit, values]);

  return (
    <FormContext.Provider value={{
      values,
      errors,
      setFieldValue,
      validate
    }}>
      <form onSubmit={handleSubmit}>
        {children}
      </form>
    </FormContext.Provider>
  );
}

export const useForm = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useForm must be used within a FormProvider');
  }
  return context;
};
